import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from "axios";
import store from "../store/Index";
import { logout, setSessionExpired } from "../store/slice/AuthSlice";

interface ApiError {
  message?: string;
  errors?: {
    user?: {
      email?: string[];
    };
  };
  [key: string]: unknown;
}

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean;
}

export const BaseURL =
  process.env.REACT_APP_API_BASE_URL || "https://staging.muzukuru.com";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    // Initialize Axios instance with the correct baseURL property
    this.api = axios.create({
      baseURL: BaseURL, // Corrected from BaseURL to baseURL
      headers: new AxiosHeaders({
        "Content-Type": "application/json",
      }),
    });

    // Request interceptor to add authorization token
    this.api.interceptors.request.use(
      (config: CustomAxiosRequestConfig) => {
        if (!config.skipAuth) {
          const token = store.getState().auth.access_token;
          if (token) {
            (config.headers as AxiosHeaders).set(
              "Authorization",
              `Bearer ${token}`,
            );
          }
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor to handle API errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError<ApiError>) => {
        const errorData = error.response?.data || {};
        const message =
          errorData.message ||
          errorData?.errors?.user?.email?.[0] ||
          "An error occurred";

        console.error("API Error:", {
          message,
          status: error.response?.status,
          headers: error.response?.headers,
          requestData: error.config?.data,
          url: error.config?.url,
          responseData: errorData,
        });

        // A 401 only means "session expired" if we were previously
        // authenticated (had a token attached to this request) — a login
        // attempt with wrong credentials also 401s but there's no prior
        // session to have expired, so it's left to LoginPage's own handling.
        if (error.response?.status === 401 && store.getState().auth.access_token) {
          store.dispatch(setSessionExpired(true));
          store.dispatch(logout());
        }

        return Promise.reject(error);
      },
    );
  }

  // Generic GET request
  async get<T>(
    url: string,
    params?: object,
    skipAuth: boolean = false,
  ): Promise<AxiosResponse<T>> {
    try {
      const config: CustomAxiosRequestConfig = {
        skipAuth,
        params,
        headers: new AxiosHeaders(),
      };
      return await this.api.get<T>(url, config);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Generic POST request
  async post<T>(
    url: string,
    data?: object | FormData,
    skipAuth: boolean = false,
  ): Promise<AxiosResponse<T>> {
    try {
      // Specific validation for Google OAuth
      if (url.includes("auth/google") && (!data || !("code" in data))) {
        throw new Error("Google OAuth requires an authorization code");
      }
      const config: CustomAxiosRequestConfig = {
        skipAuth,
        headers: new AxiosHeaders(), // Initialize with AxiosHeaders
      };
      if (data instanceof FormData) {
        (config.headers as AxiosHeaders).set(
          "Content-Type",
          "multipart/form-data",
        );
      }
      return await this.api.post<T>(url, data, config);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Generic PUT request
  async put<T>(
    url: string,
    data?: object | FormData,
    skipAuth: boolean = false,
  ): Promise<AxiosResponse<T>> {
    try {
      const config: CustomAxiosRequestConfig = {
        skipAuth,
        headers: new AxiosHeaders(), // Initialize with AxiosHeaders
      };
      if (data instanceof FormData) {
        (config.headers as AxiosHeaders).set(
          "Content-Type",
          "multipart/form-data",
        );
      }
      return await this.api.put<T>(url, data, config);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Generic PATCH request
  async patch<T>(
    url: string,
    data?: object | FormData,
    skipAuth: boolean = false,
  ): Promise<AxiosResponse<T>> {
    try {
      const config: CustomAxiosRequestConfig = {
        skipAuth,
        headers: new AxiosHeaders(), // Initialize with AxiosHeaders
      };
      if (data instanceof FormData) {
        (config.headers as AxiosHeaders).set(
          "Content-Type",
          "multipart/form-data",
        );
      }
      return await this.api.patch<T>(url, data, config);
    } catch (error) {
      console.log("PATCH request error:", error);
      throw this.handleError(error);
    }
  }

  // Generic DELETE request
  async delete<T>(
    url: string,
    skipAuth: boolean = false,
  ): Promise<AxiosResponse<T>> {
    try {
      const config: CustomAxiosRequestConfig = {
        skipAuth,
        headers: new AxiosHeaders(),
      };
      return await this.api.delete<T>(url, config);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Private helper to handle errors consistently
  private handleError(error: unknown): Error | AxiosError {
    if (error instanceof AxiosError) {
      return error;
    }
    return new Error("An unknown error occurred");
  }
}

const apiService = new ApiService();
export default apiService;
